import * as React from "react";

import { Divider, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { format } from "date-fns-tz";
import {
  DocumentUpload as DocumentUploadIcon,
  DocumentDownload as DocumentDownloadIcon,
} from "iconsax-react";
import { useTranslation } from "react-i18next";
import { DocumentCustomerPortal } from "Util/enums";
import { formatSimpleDate } from "Util/formatDateAndTime";

import {
  BadgeStatus,
  ButtonText,
  Container,
  ContainerButtonFile,
  ContainerColumnDates,
  ContainerGapDates,
  ContainerInfo,
  ContainerTitle,
  GridDates,
  SimpleText,
  SubTitleInfo,
  Title,
  TitleInfo,
} from "./styles";

interface ItemDocumentProps {
  title: string;
  status: DocumentCustomerPortal;
  url?: string | null;
  dueDate?: string | null;
  uploadDate?: string | null;
  isLoading?: boolean;
  onUploadDocument?: (uri: string, fileName: string, extension: string) => void;
}
const MAX_SIZE = 10 * 1024 * 1024;

const ItemDocument = (props: ItemDocumentProps) => {
  const { title, status, uploadDate, dueDate, onUploadDocument, url, isLoading } = props;
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));
  const { t: translate } = useTranslation();

  const renderDueLabel = () => {
    if (!dueDate) return;

    const date = formatSimpleDate(dueDate);

    return (
      <ContainerColumnDates>
        <TitleInfo>{translate("dueDate")}</TitleInfo>
        <SubTitleInfo>{date}</SubTitleInfo>
      </ContainerColumnDates>
    );
  };

  const renderDivider = () => {
    if (status !== DocumentCustomerPortal.UPLOADED || !uploadDate || !dueDate) return;

    return <Divider orientation={matches ? "vertical" : "horizontal"} flexItem />;
  };

  const renderUploadedLabel = () => {
    if (status !== DocumentCustomerPortal.UPLOADED || !uploadDate) return;

    return (
      <ContainerColumnDates>
        <TitleInfo>{translate("uploadDateAndTime")}</TitleInfo>
        <ContainerGapDates>
          <SubTitleInfo>
            {format(new Date(uploadDate), "dd/MM/yyyy • hh:mm aaaaa'm'").toUpperCase()}
          </SubTitleInfo>
        </ContainerGapDates>
      </ContainerColumnDates>
    );
  };
  const fileInput: React.LegacyRef<HTMLInputElement> = React.useRef(null);

  const onClickButton = () => {
    if (isLoading) return;

    if (status === DocumentCustomerPortal.MISSING_SHIPMENT) return;

    if (status === DocumentCustomerPortal.MISSING) {
      return fileInput.current?.click();
    }

    if (url) {
      const link = document.createElement("a");
      const target = "_blank";

      link.href = url;
      link.download = "";
      link.target = target;
      document.body.appendChild(link); // required for firefox

      link.click();
      link.remove();
      window.open(url, target)?.focus();
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isLoading) return;
    if (!event?.target?.files?.length) return;
    if (status === DocumentCustomerPortal.MISSING_SHIPMENT) return;
    const file: File = event.target.files[0];

    const splittedFileName = file.name.split(".");
    const extensionFromName = splittedFileName.at(-1);
    const fileName = splittedFileName.slice(0, -1).join("");

    const validSize = file.size < MAX_SIZE;

    if (!validSize) {
      NotificationToast.error(translate("theFileMustBeLessThan2mbInSize"));
    }

    if (validSize) {
      const reader = new FileReader();

      reader.addEventListener("load", () => {
        const uri = reader.result?.toString();

        if (uri) {
          onUploadDocument?.(uri, fileName, extensionFromName!);
        }
      });
      reader.readAsDataURL(file);
    }
    event.target.value = "";
  };

  const renderButtonText = () => {
    if (isLoading) return <SimpleText>{translate("loading")}</SimpleText>;

    if ([DocumentCustomerPortal.MISSING, DocumentCustomerPortal.MISSING_SHIPMENT].includes(status))
      return <SimpleText>{translate("upload")}</SimpleText>;

    return <SimpleText>{translate("viewFile")}</SimpleText>;
  };

  const renderIcon = () => {
    if (isLoading) return <CircularProgress size={20} />;

    if ([DocumentCustomerPortal.MISSING, DocumentCustomerPortal.MISSING_SHIPMENT].includes(status))
      return <DocumentUploadIcon size={20} color={theme.palette.primary.main} />;

    return <DocumentDownloadIcon size={20} color={theme.palette.primary.main} />;
  };

  return (
    <Container>
      <ContainerTitle>
        <Title>{title}</Title>

        <BadgeStatus type={status}>{translate(status)}</BadgeStatus>
      </ContainerTitle>

      <ContainerInfo>
        <GridDates>
          {renderDueLabel()}
          {renderDivider()}
          {renderUploadedLabel()}
        </GridDates>

        <ContainerButtonFile
          disabled={[
            DocumentCustomerPortal.MISSING_SHIPMENT,
            DocumentCustomerPortal.CANCELLED,
          ].includes(status)}
        >
          {renderIcon()}
          <ButtonText onClick={onClickButton}>{renderButtonText()}</ButtonText>

          <input
            type="file"
            ref={fileInput}
            value=""
            accept=".pdf,.csv,image/*,.xls,.xlsx,.xlsm,.xlt,.json,.doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            hidden={true}
            onChange={handleChange}
          />
        </ContainerButtonFile>
      </ContainerInfo>
    </Container>
  );
};

export { ItemDocument };
